import { useState, useMemo } from 'react';
import {
  getAllArticles,
  getAllCategories,
  searchArticles,
} from '../../data/blog';
import { BlogCard } from './BlogCard';

interface BlogHomePageProps {
  onNavigate: (path: string, view: string, tab?: string) => void;
  isStatic?: boolean;
}

const ARTICLES_PER_PAGE = 9;

export const BlogHomePage: React.FC<BlogHomePageProps> = ({ onNavigate, isStatic }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const categories = useMemo(() => getAllCategories(), []);
  const allArticles = useMemo(() => getAllArticles(), []);

  // Filtered articles
  const filteredArticles = useMemo(() => {
    return searchArticles(searchQuery, selectedCategory);
  }, [searchQuery, selectedCategory]);

  // Featured article: First article (How to calculate pipe size) or first search result
  const featuredArticle = useMemo(() => {
    if (selectedCategory === 'all' && !searchQuery) {
      return allArticles[0];
    }
    return filteredArticles[0] || null;
  }, [allArticles, filteredArticles, selectedCategory, searchQuery]);

  // Exclude featured article from grid if in default all view
  const gridArticles = useMemo(() => {
    if (selectedCategory === 'all' && !searchQuery && featuredArticle) {
      return filteredArticles.filter((a) => a.slug !== featuredArticle.slug);
    }
    return filteredArticles;
  }, [filteredArticles, featuredArticle, selectedCategory, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(gridArticles.length / ARTICLES_PER_PAGE) || 1;
  const paginatedArticles = useMemo(() => {
    if (isStatic) {
      return gridArticles;
    }
    const start = (currentPage - 1) * ARTICLES_PER_PAGE;
    return gridArticles.slice(start, start + ARTICLES_PER_PAGE);
  }, [gridArticles, currentPage, isStatic]);

  const handleCategoryChange = (slug: string) => {
    setSelectedCategory(slug);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-[#F1F6F9] dark:bg-[#111827] text-slate-800 dark:text-slate-100 transition-colors">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white via-white to-[#F1F6F9] dark:from-[#1F2937] dark:via-[#1F2937] dark:to-[#111827] border-b border-slate-200/80 dark:border-slate-700/80 pt-10 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Standards Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-950/50 border border-teal-200/70 dark:border-teal-800 text-[#087F9C] dark:text-[#39BFD3] text-xs font-semibold mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Peer-Reviewed Engineering Knowledge Hub • 30 Technical Guides</span>
            </div>

            <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#10283D] dark:text-white tracking-tight leading-tight">
              Engineering <span className="text-[#087F9C] dark:text-[#39BFD3]">Knowledge Hub</span>
            </h1>

            <p className="mt-3 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Research-backed engineering articles, formula derivations, dimensional reference standards, and practical worked sizing examples for building services engineers, mechanical designers, and plumbing contractors.
            </p>

            {/* Real-time Search Input */}
            <div className="mt-6 relative max-w-xl">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <svg
                  className="w-5 h-5 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search articles by keyword (e.g. Darcy, copper, velocity, WSFU)..."
                aria-label="Search engineering articles"
                className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white dark:bg-[#182332] text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 border border-slate-200 dark:border-slate-700 shadow-xs focus:outline-none focus:ring-2 focus:ring-[#087F9C] dark:focus:ring-[#39BFD3]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-xs font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-10">
        {/* Category Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {categories.map((cat) => {
            const isActive = selectedCategory === cat.slug;
            return (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-200 flex items-center gap-2 ${
                  isActive
                    ? 'bg-[#10283D] text-white shadow-md border border-slate-700'
                    : 'bg-white dark:bg-[#182332] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700'
                }`}
              >
                <span>{cat.title}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    isActive
                      ? 'bg-[#087F9C] text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}
                >
                  {cat.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Featured Article Section (Shown when on default all view and not searching) */}
        {selectedCategory === 'all' && !searchQuery && featuredArticle && (
          <section className="bg-white dark:bg-[#182332] rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-700 shadow-md">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Featured Visual */}
              <div className="w-full lg:w-1/2 aspect-[16/10] bg-slate-50 dark:bg-slate-800 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 flex items-center justify-center p-4">
                <img
                  src={`/assets/blog/${featuredArticle.svgVisualId}.svg`}
                  alt={featuredArticle.svgAlt}
                  className="w-full h-full object-contain hover:scale-102 transition-transform duration-300"
                />
              </div>

              {/* Featured Content */}
              <div className="w-full lg:w-1/2 space-y-4">
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-3 py-1 rounded-full font-bold bg-[#087F9C] text-white tracking-wider uppercase text-[10px]">
                    Featured Guide
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {featuredArticle.date} • {featuredArticle.readingTime}
                  </span>
                </div>

                <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#10283D] dark:text-white tracking-tight leading-tight">
                  <a
                    href={`/blog/${featuredArticle.slug}/`}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(`/blog/${featuredArticle.slug}/`, 'blog-article', featuredArticle.slug);
                    }}
                    className="hover:text-[#087F9C] dark:hover:text-[#39BFD3] transition-colors"
                  >
                    {featuredArticle.title}
                  </a>
                </h2>

                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                  {featuredArticle.excerpt}
                </p>

                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <a
                    href={`/blog/${featuredArticle.slug}/`}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(`/blog/${featuredArticle.slug}/`, 'blog-article', featuredArticle.slug);
                    }}
                    className="py-2.5 px-5 rounded-xl font-bold text-xs bg-[#10283D] hover:bg-[#1E3A5F] text-white shadow-md flex items-center gap-2 transition-all duration-200"
                  >
                    <span>Read Complete Engineering Guide</span>
                    <span>&rarr;</span>
                  </a>

                  <a
                    href={featuredArticle.calculatorLink}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(featuredArticle.calculatorLink, 'calculators', 'water');
                    }}
                    className="py-2.5 px-4 rounded-xl font-semibold text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
                  >
                    ⚡ Open {featuredArticle.calculatorName}
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-3">
          <div className="text-sm font-bold text-[#10283D] dark:text-white">
            {searchQuery ? (
              <span>
                Search results for &ldquo;{searchQuery}&rdquo; ({filteredArticles.length})
              </span>
            ) : selectedCategory === 'all' ? (
              <span>All Engineering Articles ({allArticles.length})</span>
            ) : (
              <span>
                Category: {categories.find((c) => c.slug === selectedCategory)?.title} ({filteredArticles.length})
              </span>
            )}
          </div>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Page {currentPage} of {totalPages}
          </span>
        </div>

        {/* Article Grid */}
        {paginatedArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {paginatedArticles.map((article) => (
              <BlogCard
                key={article.slug}
                article={article}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-[#182332] p-12 rounded-3xl border border-slate-200 dark:border-slate-700 text-center space-y-4">
            <div className="text-4xl">🔍</div>
            <h3 className="font-display font-bold text-lg text-[#10283D] dark:text-white">
              No engineering articles match your criteria
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              Try searching with a broader keyword (e.g. &ldquo;pipe&rdquo;, &ldquo;copper&rdquo;, &ldquo;flow&rdquo;, &ldquo;pressure&rdquo;) or select a different category.
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-[#087F9C] text-white hover:bg-teal-700 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-white dark:bg-[#182332] border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              &larr; Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 rounded-xl text-xs font-bold transition-all ${
                  currentPage === page
                    ? 'bg-[#10283D] text-white shadow-md border border-slate-700'
                    : 'bg-white dark:bg-[#182332] text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-white dark:bg-[#182332] border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Next &rarr;
            </button>
          </div>
        )}

        {/* Bottom Calculator Links Strip */}
        <section className="mt-12 bg-gradient-to-r from-[#10283D] to-[#087F9C] rounded-3xl p-6 sm:p-8 text-white shadow-md">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#39BFD3] block mb-1">
                Interactive Engineering Engines
              </span>
              <h3 className="font-display font-extrabold text-xl sm:text-2xl text-white">
                Verify Your Hydraulic Calculations Live
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-slate-200 max-w-2xl">
                All PipeSizingCalculator engines execute in your browser with zero API latency. Run instant Colebrook-White, Darcy-Weisbach, and continuity sizing with downloadable PDF compliance reports.
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5 shrink-0">
              <button
                type="button"
                onClick={() => onNavigate('/', 'calculators', 'water')}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-white text-[#10283D] hover:bg-slate-100 transition-colors shadow-sm"
              >
                Water Pipe Sizing
              </button>
              <button
                type="button"
                onClick={() => onNavigate('/copper-pipe-sizing/', 'calculators', 'copper')}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-[#183344] text-[#39BFD3] hover:bg-[#10283D] border border-teal-500/30 transition-colors shadow-sm"
              >
                Copper Sizing
              </button>
              <button
                type="button"
                onClick={() => onNavigate('/pressure-drop-calculator/', 'calculators', 'pressure')}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-[#183344] text-[#39BFD3] hover:bg-[#10283D] border border-teal-500/30 transition-colors shadow-sm"
              >
                Darcy Pressure Drop
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
